import type { Rarity } from '__generated__/globalTypes';
import {
  AveragePlayerScore,
  AverageStat,
  BaseballAverageStatType,
  BasketballAverageStatType,
  So5FixtureEvent,
  So5LeaderboardSeasonality,
} from '__generated__/globalTypes';
import type { PrintablePlayerSortName } from 'contexts/config';

import type { TotalAveragePlayerScore } from './averagePlayerScore';
import { StatsDisplay } from './playerSearchView';

export const averageScoreSorts: Record<
  TotalAveragePlayerScore,
  { asc: PrintablePlayerSortName; desc: PrintablePlayerSortName }
> = {
  [AveragePlayerScore.LAST_TEN_PLAYED_SO5_AVERAGE_SCORE]: {
    asc: 'ASC-L10',
    desc: 'DESC-L10',
  },
  [AveragePlayerScore.LAST_FIFTEEN_SO5_AVERAGE_SCORE]: {
    asc: 'ASC-L15',
    desc: 'DESC-L15',
  },
  [AveragePlayerScore.LAST_FIVE_SO5_AVERAGE_SCORE]: {
    asc: 'ASC-L5',
    desc: 'DESC-L5',
  },
  [AveragePlayerScore.SEASON_AVERAGE_SCORE]: {
    asc: 'ASC-season_avg',
    desc: 'DESC-season_avg',
  },
  [AveragePlayerScore.LAST_FORTY_SO5_AVERAGE_SCORE]: {
    asc: 'ASC-L40',
    desc: 'DESC-L40',
  },
};

type BreakdownScore =
  | typeof AveragePlayerScore.LAST_FIFTEEN_AVERAGE_ALL_AROUND_SCORE
  | typeof AveragePlayerScore.LAST_FIFTEEN_AVERAGE_DECISIVE_SCORE;

export const breakdownScoreSorts: Record<
  BreakdownScore,
  { asc: PrintablePlayerSortName; desc: PrintablePlayerSortName }
> = {
  [AveragePlayerScore.LAST_FIFTEEN_AVERAGE_ALL_AROUND_SCORE]: {
    asc: 'ASC-L15AA',
    desc: 'DESC-L15AA',
  },
  [AveragePlayerScore.LAST_FIFTEEN_AVERAGE_DECISIVE_SCORE]: {
    asc: 'ASC-L15D',
    desc: 'DESC-L15D',
  },
};

type Projection = typeof So5FixtureEvent.DAILY | typeof So5FixtureEvent.CLASSIC;

export const projectionSorts: Record<
  Projection,
  { asc: PrintablePlayerSortName; desc: PrintablePlayerSortName }
> = {
  [So5FixtureEvent.DAILY]: {
    asc: 'ASC-next_daily_fixture_projected_score',
    desc: 'DESC-next_daily_fixture_projected_score',
  },
  [So5FixtureEvent.CLASSIC]: {
    asc: 'ASC-next_classic_fixture_projected_score',
    desc: 'DESC-next_classic_fixture_projected_score',
  },
};

type ProjectionGrade =
  | typeof So5FixtureEvent.DAILY
  | typeof So5FixtureEvent.CLASSIC;
export const projectionGradeSorts: Record<
  ProjectionGrade,
  { asc: PrintablePlayerSortName; desc: PrintablePlayerSortName }
> = {
  // We're reversing the order because the projection grade is a letter grade
  // so we want to sort from A to F when DESC and F to A when ASC
  [So5FixtureEvent.DAILY]: {
    desc: 'ASC-next_daily_fixture_projected_grade',
    asc: 'DESC-next_daily_fixture_projected_grade',
  },
  [So5FixtureEvent.CLASSIC]: {
    desc: 'ASC-next_classic_fixture_projected_grade',
    asc: 'DESC-next_classic_fixture_projected_grade',
  },
};
export const nbaStatSorts: Record<
  BasketballAverageStatType,
  { asc: PrintablePlayerSortName; desc: PrintablePlayerSortName }
> = {
  [BasketballAverageStatType.POINTS]: {
    asc: 'ASC-points',
    desc: 'DESC-points',
  },
  [BasketballAverageStatType.REBOUNDS]: {
    asc: 'ASC-rebounds',
    desc: 'DESC-rebounds',
  },
  [BasketballAverageStatType.MINS_PLAYED]: {
    asc: 'ASC-average_mins_played',
    desc: 'DESC-average_mins_played',
  },
  [BasketballAverageStatType.STEALS]: {
    asc: 'ASC-steals',
    desc: 'DESC-steals',
  },
  [BasketballAverageStatType.ASSISTS]: {
    asc: 'ASC-assists',
    desc: 'DESC-assists',
  },
  [BasketballAverageStatType.BLOCKS]: {
    asc: 'ASC-blocks',
    desc: 'DESC-blocks',
  },
  [BasketballAverageStatType.TURNOVERS]: {
    asc: 'ASC-turnovers',
    desc: 'DESC-turnovers',
  },
  [BasketballAverageStatType.THREE_POINTS_MADE]: {
    asc: 'ASC-three_points_made',
    desc: 'DESC-three_points_made',
  },
  [BasketballAverageStatType.DOUBLE_DOUBLE]: {
    asc: 'ASC-double_double',
    desc: 'DESC-double_double',
  },
  [BasketballAverageStatType.TRIPLE_DOUBLE]: {
    asc: 'ASC-triple_double',
    desc: 'DESC-triple_double',
  },
};

export const baseballStatSorts: Record<
  BaseballAverageStatType,
  { asc: PrintablePlayerSortName; desc: PrintablePlayerSortName }
> = {
  [BaseballAverageStatType.HITTING_RUNS]: {
    asc: 'ASC-hitting_runs',
    desc: 'DESC-hitting_runs',
  },
  [BaseballAverageStatType.HITTING_RUNS_BATTED_IN]: {
    asc: 'ASC-hitting_runs_batted_in',
    desc: 'DESC-hitting_runs_batted_in',
  },
  [BaseballAverageStatType.HITTING_SINGLES]: {
    asc: 'ASC-hitting_singles',
    desc: 'DESC-hitting_singles',
  },
  [BaseballAverageStatType.HITTING_DOUBLES]: {
    asc: 'ASC-hitting_doubles',
    desc: 'DESC-hitting_doubles',
  },
  [BaseballAverageStatType.HITTING_TRIPLES]: {
    asc: 'ASC-hitting_triples',
    desc: 'DESC-hitting_triples',
  },
  [BaseballAverageStatType.HITTING_HOME_RUNS]: {
    asc: 'ASC-hitting_home_runs',
    desc: 'DESC-hitting_home_runs',
  },
  [BaseballAverageStatType.HITTING_WALKS]: {
    asc: 'ASC-hitting_walks',
    desc: 'DESC-hitting_walks',
  },
  [BaseballAverageStatType.HITTING_STRIKEOUTS]: {
    asc: 'ASC-hitting_strikeouts',
    desc: 'DESC-hitting_strikeouts',
  },
  [BaseballAverageStatType.HITTING_STOLEN_BASES]: {
    asc: 'ASC-hitting_stolen_bases',
    desc: 'DESC-hitting_stolen_bases',
  },
  [BaseballAverageStatType.HITTING_HIT_BY_PITCH]: {
    asc: 'ASC-hitting_hit_by_pitch',
    desc: 'DESC-hitting_hit_by_pitch',
  },
  [BaseballAverageStatType.HITTING_CAUGHT_STEALING]: {
    asc: 'ASC-hitting_caught_stealing',
    desc: 'DESC-hitting_caught_stealing',
  },
  [BaseballAverageStatType.HITTING_AT_BASE]: {
    asc: 'ASC-hitting_at_base',
    desc: 'DESC-hitting_at_base',
  },
  [BaseballAverageStatType.HITTING_AT_PLATE]: {
    asc: 'ASC-hitting_at_plate',
    desc: 'DESC-hitting_at_plate',
  },
  [BaseballAverageStatType.PITCHING_INNINGS_PITCHED]: {
    asc: 'ASC-pitching_innings_pitched',
    desc: 'DESC-pitching_innings_pitched',
  },
  [BaseballAverageStatType.PITCHING_STRIKEOUTS]: {
    asc: 'ASC-pitching_strikeouts',
    desc: 'DESC-pitching_strikeouts',
  },
  [BaseballAverageStatType.PITCHING_HITS_ALLOWED]: {
    asc: 'ASC-pitching_hits_allowed',
    desc: 'DESC-pitching_hits_allowed',
  },
  [BaseballAverageStatType.PITCHING_EARNED_RUNS]: {
    asc: 'ASC-pitching_earned_runs',
    desc: 'DESC-pitching_earned_runs',
  },
  [BaseballAverageStatType.PITCHING_WALKS]: {
    asc: 'ASC-pitching_walks',
    desc: 'DESC-pitching_walks',
  },
  [BaseballAverageStatType.PITCHING_HIT_BATSMEN]: {
    asc: 'ASC-pitching_hit_batsmen',
    desc: 'DESC-pitching_hit_batsmen',
  },
  [BaseballAverageStatType.PITCHING_WINS]: {
    asc: 'ASC-pitching_wins',
    desc: 'DESC-pitching_wins',
  },
  [BaseballAverageStatType.PITCHING_SAVES]: {
    asc: 'ASC-pitching_saves',
    desc: 'DESC-pitching_saves',
  },
  [BaseballAverageStatType.PITCHING_HOLDS]: {
    asc: 'ASC-pitching_holds',
    desc: 'DESC-pitching_holds',
  },
  [BaseballAverageStatType.PITCHING_NO_HITTERS]: {
    asc: 'ASC-pitching_no_hitters',
    desc: 'DESC-pitching_no_hitters',
  },
  [BaseballAverageStatType.PITCHING_PITCH_COUNT]: {
    asc: 'ASC-pitching_pitch_count',
    desc: 'DESC-pitching_pitch_count',
  },
  [BaseballAverageStatType.PITCHING_RELIEF_APPEARANCE]: {
    asc: 'ASC-pitching_relief_appearance',
    desc: 'DESC-pitching_relief_appearance',
  },
};

export const footballStatSorts = (
  statsDisplay: StatsDisplay
): Record<
  string,
  { asc: PrintablePlayerSortName; desc: PrintablePlayerSortName }
> => {
  const prefix = statsDisplay === StatsDisplay.TOTAL ? 'total' : 'average';
  return {
    [AverageStat.MINS_PLAYED]: {
      asc: `ASC-${prefix}_mins_played`,
      desc: `DESC-${prefix}_mins_played`,
    },
    [AverageStat.SET_PIECE_TAKEN]: {
      asc: `ASC-${prefix}_set_piece_taken`,
      desc: `DESC-${prefix}_set_piece_taken`,
    },
    [AverageStat.GAME_STARTED]: {
      asc: `ASC-${prefix}_game_started`,
      desc: `DESC-${prefix}_game_started`,
    },
    [AverageStat.ACCURATE_PASS]: {
      asc: `ASC-${prefix}_accurate_pass`,
      desc: `DESC-${prefix}_accurate_pass`,
    },
    [AverageStat.WON_TACKLE]: {
      asc: `ASC-${prefix}_won_tackle`,
      desc: `DESC-${prefix}_won_tackle`,
    },
    [AverageStat.INTERCEPTION_WON]: {
      asc: `ASC-${prefix}_interception_won`,
      desc: `DESC-${prefix}_interception_won`,
    },
    [AverageStat.GOALS]: {
      asc: `ASC-${prefix}_goals`,
      desc: `DESC-${prefix}_goals`,
    },
    [AverageStat.GOAL_ASSIST]: {
      asc: `ASC-${prefix}_goal_assist`,
      desc: `DESC-${prefix}_goal_assist`,
    },
    [AverageStat.ASSIST_PENALTY_WON]: {
      asc: `ASC-${prefix}_assist_penalty_won`,
      desc: `DESC-${prefix}_assist_penalty_won`,
    },
    [AverageStat.LAST_MAN_TACKLE]: {
      asc: `ASC-${prefix}_last_man_tackle`,
      desc: `DESC-${prefix}_last_man_tackle`,
    },
    [AverageStat.CLEAN_SHEET]: {
      asc: `ASC-${prefix}_clean_sheet`,
      desc: `DESC-${prefix}_clean_sheet`,
    },
    [AverageStat.CLEAN_SHEET_60]: {
      asc: `ASC-${prefix}_clean_sheet_60`,
      desc: `DESC-${prefix}_clean_sheet_60`,
    },
    [AverageStat.YELLOW_CARD]: {
      asc: `ASC-${prefix}_yellow_card`,
      desc: `DESC-${prefix}_yellow_card`,
    },
    [AverageStat.RED_CARD]: {
      asc: `ASC-${prefix}_red_card`,
      desc: `DESC-${prefix}_red_card`,
    },
    [AverageStat.ERROR_LEAD_TO_GOAL]: {
      asc: `ASC-${prefix}_error_lead_to_goal`,
      desc: `DESC-${prefix}_error_lead_to_goal`,
    },
    [AverageStat.PENALTY_CONCEDED]: {
      asc: `ASC-${prefix}_penalty_conceded`,
      desc: `DESC-${prefix}_penalty_conceded`,
    },
    [AverageStat.OWN_GOALS]: {
      asc: `ASC-${prefix}_own_goals`,
      desc: `DESC-${prefix}_own_goals`,
    },
    [AverageStat.THREE_GOALS_CONCEDED]: {
      asc: `ASC-${prefix}_three_goals_conceded`,
      desc: `DESC-${prefix}_three_goals_conceded`,
    },
  };
};

const getSeasonalityPrefix = (seasonality: So5LeaderboardSeasonality) =>
  seasonality === So5LeaderboardSeasonality.IN_SEASON
    ? 'in_season'
    : 'all_seasons';

export const priceSorts = (
  rarity:
    | typeof Rarity.limited
    | typeof Rarity.rare
    | typeof Rarity.super_rare
    | typeof Rarity.unique,
  seasonality: So5LeaderboardSeasonality
): { asc: PrintablePlayerSortName; desc: PrintablePlayerSortName } => {
  const seasonalityPrefix = getSeasonalityPrefix(seasonality);
  return {
    asc: `ASC-floor_prices_${seasonalityPrefix}_${rarity}`,
    desc: `DESC-floor_prices_${seasonalityPrefix}_${rarity}`,
  };
};

export const floorPricesAttributes = (
  rarity:
    | typeof Rarity.limited
    | typeof Rarity.rare
    | typeof Rarity.super_rare
    | typeof Rarity.unique,
  seasonality: So5LeaderboardSeasonality
): string => `floor_prices.${getSeasonalityPrefix(seasonality)}.${rarity}`;
